import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { StatusPill } from "../../components/common/StatusPill";
import { ErrorState } from "../../components/common/ErrorState";
import { toast } from "../../components/ui/sonner";
import { useCelebration } from "../../contexts/CelebrationContext";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

const STATUS_FILTERS = [
  { value: "pending", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" },
  { value: "all", label: "All requests" },
];

// UX-TRIAL §1 "Platform Operator approval queue (mounts into the CP-01/CP-02 shell)" +
// verification item 6: list of held `trial_request`s showing WHICH signal tripped (platform-wide
// vs per-signal rate limit, or a soft flag like fast_submit/gym_name_reuse) so the operator can
// judge quickly. Also hosts the §2B dual rate-limit config (platform-wide + per-signal daily
// thresholds) — both explicitly required to be Kairoo-configurable, not hardcoded.
export default function TrialManagement() {
  const [status, setStatus] = useState("pending");
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyReason, setDenyReason] = useState("");
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();

  const requestsQ = useQuery({
    queryKey: ["control-trial-requests", status],
    queryFn: async () => (await api.get("/control/trial/requests", { params: status === "all" ? {} : { status } })).data.requests,
  });

  const configQ = useQuery({
    queryKey: ["control-trial-config"],
    queryFn: async () => (await api.get("/control/trial/config")).data,
  });

  const [configForm, setConfigForm] = useState(null);
  const config = configForm || configQ.data;

  const approveMutation = useMutation({
    mutationFn: (id) => api.post(`/control/trial/requests/${id}/approve`),
    onSuccess: () => {
      celebrate("gym_provisioned", "Real tenant provisioned and the owner has been emailed.");
      queryClient.invalidateQueries({ queryKey: ["control-trial-requests"] });
      queryClient.invalidateQueries({ queryKey: ["control-gyms"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const denyMutation = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/control/trial/requests/${id}/deny`, { reason: reason || null }),
    onSuccess: () => {
      toast.success("Trial request denied.");
      queryClient.invalidateQueries({ queryKey: ["control-trial-requests"] });
      setDenyTarget(null);
      setDenyReason("");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const configMutation = useMutation({
    mutationFn: () => api.patch("/control/trial/config", {
      platform_daily_limit: Number(config.platform_daily_limit),
      per_signal_daily_limit: Number(config.per_signal_daily_limit),
    }),
    onSuccess: (res) => {
      toast.success("Trial rate limits updated.");
      setConfigForm(null);
      queryClient.setQueryData(["control-trial-config"], res.data);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const rows = useMemo(() => requestsQ.data || [], [requestsQ.data]);

  if (requestsQ.isError) return <ErrorState error={requestsQ.error} onRetry={() => requestsQ.refetch()} title="Couldn't load trial requests" />;

  return (
    <div data-testid={CONTROL.trialsPage} className="space-y-6 max-w-6xl pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Applications</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
          Incoming gym applications — review, then approve to provision the gym and email login details to the owner.
        </p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="solid-surface rounded-[14px] p-5 space-y-4">
        <p className="eyebrow">Rate-limit config</p>
        <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
          Either counter tripping today routes further signups to this queue instead of instant provisioning.
        </p>
        {config && (
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1.5">
              <Label htmlFor="trial-cfg-platform">Platform-wide daily limit</Label>
              <Input
                id="trial-cfg-platform" type="number" min={1} className="w-40"
                data-testid={CONTROL.trialConfigPlatformInput}
                value={config.platform_daily_limit}
                onChange={(e) => setConfigForm({ ...config, platform_daily_limit: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="trial-cfg-signal">Per-signal daily limit (IP / phone / email domain)</Label>
              <Input
                id="trial-cfg-signal" type="number" min={1} className="w-40"
                data-testid={CONTROL.trialConfigPerSignalInput}
                value={config.per_signal_daily_limit}
                onChange={(e) => setConfigForm({ ...config, per_signal_daily_limit: e.target.value })}
              />
            </div>
            <Button
              data-testid={CONTROL.trialConfigSaveButton}
              disabled={configMutation.isPending}
              onClick={() => configMutation.mutate()}
              className="text-white"
              style={{ background: "var(--scarlet-accent)" }}
            >
              Save limits
            </Button>
          </div>
        )}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="flex items-center justify-between flex-wrap gap-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={CONTROL.statusFilter} className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.09)} className="solid-surface rounded-[14px] overflow-hidden">
        <Table data-testid={CONTROL.trialsQueueTable}>
          <TableHeader>
            <TableRow>
              <TableHead>Gym</TableHead>
              <TableHead>Owner / contact</TableHead>
              <TableHead>Signal tripped</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requestsQ.isLoading && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Loading trial requests…</TableCell></TableRow>
            )}
            {!requestsQ.isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No trial requests match this filter.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r._id} data-testid={CONTROL.trialRow(r._id)}>
                <TableCell className="font-medium" style={{ color: "var(--scarlet-text)" }}>{r.gym_name}</TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>
                  <div>{r.owner_name}</div>
                  <div className="text-xs">{r.email} · {r.phone}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.trip_reason && <Badge variant="outline">{r.trip_reason.replace("soft_flag:", "")}</Badge>}
                    {!r.trip_reason && <span className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>—</span>}
                  </div>
                </TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }} className="text-xs">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell><StatusPill status={r.status === "denied" ? "overdue" : r.status === "approved" ? "active" : "expiring"} label={r.status} /></TableCell>
                <TableCell>
                  {r.status === "pending" ? (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm" data-testid={CONTROL.trialApproveButton(r._id)}
                        disabled={approveMutation.isPending && approveMutation.variables === r._id}
                        onClick={() => approveMutation.mutate(r._id)}
                        className="text-white gap-1" style={{ background: "var(--scarlet-accent)" }}
                      >
                        <Check size={13} /> Approve
                      </Button>
                      <Button
                        size="sm" variant="outline" data-testid={CONTROL.trialDenyButton(r._id)}
                        onClick={() => setDenyTarget(r)}
                        style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}
                        className="gap-1"
                      >
                        <X size={13} /> Deny
                      </Button>
                    </div>
                  ) : <span className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      <Dialog open={!!denyTarget} onOpenChange={(v) => !v && setDenyTarget(null)}>
        <DialogContent className="glass-modal" data-testid={CONTROL.trialDenyDialog}>
          <DialogHeader>
            <DialogTitle>Deny {denyTarget?.gym_name}'s trial request?</DialogTitle>
            <DialogDescription>A polite decline email is sent; the request is kept for pattern analysis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="deny-reason">Reason (optional, internal)</Label>
            <Textarea id="deny-reason" data-testid={CONTROL.trialDenyReasonInput} value={denyReason} onChange={(e) => setDenyReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button
              data-testid={CONTROL.trialDenyConfirm}
              disabled={denyMutation.isPending}
              onClick={() => denyMutation.mutate({ id: denyTarget._id, reason: denyReason })}
              style={{ background: "var(--scarlet-neg)" }}
              className="text-white"
            >
              Deny request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
